import React from 'react';

import ReactDOM from 'react-dom/client';

import { ServiceProvider } from '@/contexts';
import { openStartTab } from '@features/workspace/launch';
import { captureInstalledApp, takeLaunch } from '@shared/app-identity/installedApp';
import SyncBrowserRouter from '@shared/components/SyncBrowserRouter';
import { ToastProvider, UndoSnackbarProvider } from '@shared/components/ui';
import {
  installFlushTriggers,
  listenForEntityMoves,
  listenForInvalidations,
  listenForWipes,
  wipePendingOnBoot,
} from '@shared/offline';
import { installStaleChunkReload } from '@shared/utils/staleChunkReload';

import App from './App';
import './index.css';
import './styles/animations.css';
// The journal route is a lazy chunk: a flush at boot, on reconnect, or after a
// sign-in on another page would otherwise find no policy for its queued rows
// and fail every one of them. Register the journal's write policies up front.
import '@features/journal/services/journalWritePolicies';

/** Resolve the effective theme mode from cached settings and legacy localStorage. */
const resolveThemeMode = (): string => {
  const cachedSettings = localStorage.getItem('pa-app-storage');
  let themeMode = 'system';

  if (cachedSettings) {
    try {
      const parsed = JSON.parse(cachedSettings);
      if (parsed?.state?.settings?.theme_mode) {
        themeMode = parsed.state.settings.theme_mode;
      }
    } catch {
      // Ignore parsing errors, fall back to default behavior
    }
  }

  // If no theme_mode in settings, check old localStorage theme for migration
  if (themeMode === 'system' && localStorage.theme) {
    themeMode = localStorage.theme;
  }

  return themeMode;
};

/** Apply the resolved theme mode to the document element. */
const applyThemeMode = (themeMode: string): void => {
  if (themeMode === 'dark') {
    document.documentElement.classList.add('dark');
  } else if (themeMode === 'light') {
    document.documentElement.classList.remove('dark');
  } else {
    // System mode - check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.classList.toggle('dark', prefersDark);
  }
};

// Initialize dark mode from theme settings or fallback to system preference
const initDarkMode = (): void => {
  applyThemeMode(resolveThemeMode());
};

initDarkMode();

// Before React: a tab that outlives a deploy still holds the old entry bundle, and its
// first navigation into an unvisited route 404s on a chunk the new image no longer
// ships. Reload onto the current bundle instead of dropping into the error boundary.
installStaleChunkReload();

// Before React and before routing: an in-app navigation drops the query string,
// so the launch marker must be read while it is still on the URL.
captureInstalledApp();

// A marked launch is a fresh intent: open-or-focus the app's start tab in the
// shared workspace store (nav spec §4.10) instead of letting the bar overwrite
// whatever tab the browser last had active.
const launchedApp = takeLaunch();
if (launchedApp) openStartTab(launchedApp);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element');
}

// Listen first, unconditionally: another tab can wipe at any moment, and a
// pending wipe of our own must not decide whether we hear it. A browser-close
// logout cannot await IndexedDB, so it leaves a flag; honour that before
// anything opens the store. A delete that fails keeps the flag for the next
// boot, so swallow its rejection rather than leaving it unhandled.
listenForWipes();
void wipePendingOnBoot().catch(() => undefined);

// Another tab's flush can land this tab's queued save, and another tab's
// conflict handler can move the entry this tab is editing: hear both.
listenForInvalidations();
listenForEntityMoves();

// Replays queued writes on boot, reconnect, tab focus, sign-in, and another tab's request.
installFlushTriggers();

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ServiceProvider>
      {/* SyncBrowserRouter is a drop-in BrowserRouter without startTransition.
         See docs/lessons-learned/react-router-tiptap-concurrent-nav-block.md */}
      <SyncBrowserRouter>
        <ToastProvider>
          <UndoSnackbarProvider>
            <App />
          </UndoSnackbarProvider>
        </ToastProvider>
      </SyncBrowserRouter>
    </ServiceProvider>
  </React.StrictMode>
);
